import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * Visible FAQ section on the Home page. Crawlers (Google, Cytd, LLMs) reward
 * real, human-readable Q&A over schema.org markup alone. The JSON-LD FAQPage
 * schema in /public/index.html mirrors these answers exactly.
 */
const FAQ_ITEMS = [
  {
    q: 'What does Keystone Strategic Consultants actually do?',
    a: 'Keystone is a UK-based specialist delivery consultancy for complex, regulated environments — NHS Capital, PFI Hard Services, Healthcare FM and Tier-1 Construction. We diagnose delivery, embed alongside your teams, and build the entire operating model inside Videra, our digital delivery platform. When we hand back, the platform keeps the discipline running.',
  },
  {
    q: 'What is the Videra platform?',
    a: 'Videra is Keystone\u2019s digital delivery operating system. It provides stage-gated governance, AI-powered board and executive reporting, and audit-ready evidence packs with a 60-year Golden Thread aligned to the Building Safety Act 2022. Sector-tuned workspaces are available for NHS Capital, Healthcare FM/PFI and Tier-1 Construction (Construction+).',
  },
  {
    q: 'What is a delivery framework and why does complex delivery need one?',
    a: 'A delivery framework is a mapped, stage-gated set of workflows, roles, evidence requirements and decision points that every project follows from initiation to hand-back. In complex, regulated environments — NHS capital, PFI hard services, Tier-1 construction — it replaces ad-hoc processes with a single, disciplined and auditable pathway, so risk is surfaced early, governance is applied consistently and evidence assembles as a by-product of the work.',
  },
  {
    q: 'How does the Building Safety Act 2022 affect NHS estates and PFI hard services?',
    a: 'The Building Safety Act 2022 requires dutyholders to maintain a Golden Thread of information for higher-risk buildings — every design decision, safety justification and change captured, retained and made accessible for the life of the building (60 years for many NHS estate assets). Videra is audit-ready by design: evidence is compiled automatically from the workflow, so NHS Trusts, SPVs and FM operators can demonstrate compliance without manually assembling packs at inspection.',
  },
  {
    q: 'What is PFI hand-back readiness and how does Videra support it?',
    a: 'PFI hand-back readiness is the discipline of preparing a PFI concession for expiry — proving asset condition, lifecycle history, statutory compliance and outstanding variations, so the asset returns to the public authority without disputed liabilities. Videra runs hard services, capital projects and lifecycle intelligence on one rail, so every asset is linked to the projects that touched it and the evidence trail is intact when the concession ends.',
  },
  {
    q: 'How does a Keystone engagement work in practice?',
    a: 'Three stages. Diagnose (2\u20134 weeks) maps your delivery landscape, surfaces friction and agrees the target operating model. Embed (8\u201312 weeks) sits alongside your team and builds the governance inside Videra so the work runs in one place. Hand back leaves the team stronger with a fully configured Videra environment they own \u2014 with an optional light-touch optimisation retainer.',
  },
  {
    q: 'Can I try Videra before committing?',
    a: 'Yes \u2014 a free 14-day trial is available at demo.videra.app/explore. Pick your sector on the /explore page (NHS Capital, FM Hard Services, or Construction+) and get a live workspace seeded with realistic sample data. No credit card required.',
  },
  {
    q: 'Which sectors does Keystone serve?',
    a: 'Government, NHS Trusts and Integrated Care Boards, Facilities Management operators, PFI concessions and SPVs, Tier-1 main contractors, soft services and specialist contractors, and Project Management Consultancies who want to give their clients a governed delivery platform.',
  },
];

const HomeFAQ = () => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section
      id="faq"
      data-testid="v2-home-faq"
      className="py-20 md:py-24 bg-white border-t border-slate-200"
    >
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12 md:mb-14 max-w-2xl">
          <p className="text-xs uppercase tracking-[0.2em] text-blue-600 font-semibold mb-4">
            Frequently asked questions
          </p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900 leading-[1.08]">
            Straight answers on delivery, governance and Videra.
          </h2>
          <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed">
            Common questions from NHS Trusts, PFI operators, FM providers and Tier-1
            contractors — answered plainly.
          </p>
        </div>

        <div className="divide-y divide-slate-200 border-y border-slate-200">
          {FAQ_ITEMS.map((item, i) => {
            const open = i === openIdx;
            return (
              <article
                key={item.q}
                itemScope
                itemType="https://schema.org/Question"
                data-testid={`v2-home-faq-item-${i}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  aria-expanded={open}
                  className="w-full flex items-start justify-between gap-6 py-5 md:py-6 text-left group focus:outline-none"
                >
                  <h3
                    itemProp="name"
                    className="text-base md:text-lg font-medium text-slate-900 leading-snug group-hover:text-blue-700 transition-colors"
                  >
                    {item.q}
                  </h3>
                  <ChevronDown
                    className={`w-5 h-5 flex-shrink-0 text-slate-400 mt-0.5 transition-transform ${open ? 'rotate-180 text-blue-600' : ''}`}
                  />
                </button>
                {open && (
                  <div
                    itemProp="acceptedAnswer"
                    itemScope
                    itemType="https://schema.org/Answer"
                    className="pb-6 pr-10"
                  >
                    <p
                      itemProp="text"
                      className="text-slate-600 leading-relaxed text-[15px] md:text-base"
                    >
                      {item.a}
                    </p>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export { FAQ_ITEMS };
export default HomeFAQ;
